<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            :loading="loading"
            rowClass="text-center"
            :perPage="itemsPerPage"
            :sortBy="sortColumn"
            :sortOrder="sortDirection"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <!-- Export Button -->
                    <ExportSelect
                        :label="$t('customers.export')"
                        :options="[
                            { label: $t('customers.csv'), value: 'csv' },
                            { label: $t('customers.excel'), value: 'excel' },
                        ]"
                        :disabled="exportLoading || !hasAccess"
                        :badge="!hasAccess ? $t('subscription_info.pro') : ''"
                        :loading="exportLoading"
                        @export-selected="hasAccess ? downloadCustomers($event) : null"
                    />

                    <!-- Import Button -->
                    <button
                        v-if="isSuperAdmin"
                        type="button"
                        @click="openImportPopup"
                        class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                    >
                        <Upload class="ppv2-w-5 ppv2-h-5" />
                        <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('customers.import') }}
                        </span>
                    </button>

                    <!-- Merge Button
                    <a
                        href="/back-office/customer/merge"
                        class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                    >
                        <GitMerge class="ppv2-w-5 ppv2-h-5" />
                        <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('customers.merge') }}
                        </span>
                    </a> -->

                    <div class="ppv2-flex-1"></div>

                    <!-- Column Visibility Toggle -->
                    <div class="ppv2-relative" ref="columnsDropdown">
                        <button
                            @click="showColumns = !showColumns"
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <FilePen class="ppv2-w-5 ppv2-h-5" />
                            <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('customers.columns') }}
                            </span>
                            <ChevronDown class="ppv2-w-4 ppv2-h-4" />
                        </button>
                        <!-- Dropdown -->
                        <div
                            v-if="showColumns"
                            class="menuDropdown ppv2-absolute ppv2-top-full ppv2-right-0 ppv2-mt-2 ppv2-rounded-md ppv2-overflow-hidden ppv2-z-50"
                        >
                            <div class="ppv2-max-h-[300px] ppv2-overflow-y-auto">
                                <label
                                    v-for="(column, index) in allColumns"
                                    :key="index"
                                    class="dropdownItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-1 ppv2-cursor-pointer ppv2-transition-colors"
                                >
                                    <BaseCheckbox
                                        :value="selectedColumnKeys.includes(column.key)"
                                        @input="toggleColumn(column.key)"
                                        size="sm"
                                        :hasBorderChecked="true"
                                    />
                                    <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">{{
                                        column.label
                                    }}</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="navigateToCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('customers.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: First Name -->
            <template #column-first_name="{ item }">
                <a
                    :href="item.is_guest == 1 ? 'javascript:void(0)' : `/back-office/v3/customer/${item.id}/edit`"
                    class="ppv2-text-black poppinsRegular ppv2-transition-colors"
                    :class="{
                        'ppv2-no-underline ppv2-pointer-events-none': item.is_guest == 1,
                        'hover:ppv2-text-[#7D2DBD] ppv2-underline': item.is_guest != 1,
                    }"
                    :style="{ fontSize: normalizeFontSize(14) }"
                    @click="item.is_guest == 1 ? $event.preventDefault() : null"
                >
                    {{ item.first_name }}
                </a>
            </template>

            <!-- Column: Last Name -->
            <template #column-last_name="{ item }">
                <a
                    :href="item.is_guest == 1 ? 'javascript:void(0)' : `/back-office/v3/customer/${item.id}/edit`"
                    class="ppv2-text-black poppinsRegular ppv2-transition-colors"
                    :class="{
                        'ppv2-no-underline ppv2-pointer-events-none': item.is_guest == 1,
                        'hover:ppv2-text-[#7D2DBD] ppv2-underline': item.is_guest != 1,
                    }"
                    :style="{ fontSize: normalizeFontSize(14) }"
                    @click="item.is_guest == 1 ? $event.preventDefault() : null"
                >
                    {{ item.last_name }}
                </a>
            </template>

            <!-- Column: Account (Client / Invité) -->
            <template #column-account="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                    item.is_guest == 1 ? $t('customers.fields.guest') : $t('customers.fields.client')
                }}</span>
            </template>

            <!-- Column: Origin -->
            <template #column-origin="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                    item.origin || $t('customers.unspecified')
                }}</span>
            </template>

            <!-- Column: Communication -->
            <template #column-communication="{ item }">
                <span class="poppinsRegular ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.is_newsletter_subscribed ? $t('customers.fields.subscribed') : $t('customers.fields.unsubscribed') }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <a
                            :href="item.is_guest == 1 ? 'javascript:void(0)' : `/back-office/v3/customer/${item.id}/edit`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="{
                                'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                'ppv2-cursor-pointer': item.is_guest != 1,
                            }"
                            @click="item.is_guest == 1 ? $event.preventDefault() : null"
                        >
                            <FilePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.edit')
                            }}</span>
                        </a>

                        <!-- Planifier réservation -->
                        <a
                            :href="
                                item.is_guest == 1
                                    ? 'javascript:void(0)'
                                    : `/back-office/planning?modaladd=1&fillclient=${item.id}`
                            "
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="{
                                'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                'ppv2-cursor-pointer': item.is_guest != 1,
                            }"
                            @click="item.is_guest == 1 ? $event.preventDefault() : null"
                        >
                            <CalendarPlus :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.scheduleReservation')
                            }}</span>
                        </a>

                        <!-- Caisse -->
                        <form
                            :action="item.is_guest == 1 ? 'javascript:void(0)' : '/back-office/caisse/customer/change'"
                            method="GET"
                            class="ppv2-m-0"
                            @submit.prevent="item.is_guest == 1 ? null : $event.target.submit()"
                        >
                            <input type="hidden" name="customerSelect" :value="item.id" />
                            <button
                                type="submit"
                                class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-w-full ppv2-text-left"
                                :class="{
                                    'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                    'ppv2-cursor-pointer': item.is_guest != 1,
                                }"
                                :disabled="item.is_guest == 1"
                            >
                                <Computer :size="16" />
                                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                    $t('customers.caisse')
                                }}</span>
                            </button>
                        </form>

                        <!-- Définir mot de passe -->
                        <a
                            :href="item.is_guest == 1 ? 'javascript:void(0)' : `/back-office/customer/${item.id}/set_password`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="{
                                'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                'ppv2-cursor-pointer': item.is_guest != 1,
                            }"
                            @click="item.is_guest == 1 ? $event.preventDefault() : null"
                        >
                            <KeyRound :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.setPassword')
                            }}</span>
                        </a>

                        <!-- Désactiver/Réactiver -->
                        <div
                            v-if="item.status_id == 1"
                            @click="item.is_guest == 1 ? null : handleDisable(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="{
                                'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                'ppv2-cursor-pointer': item.is_guest != 1,
                            }"
                        >
                            <UserX :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.disable')
                            }}</span>
                        </div>
                        <div
                            v-else
                            @click="item.is_guest == 1 ? null : handleEnable(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="{
                                'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                'ppv2-cursor-pointer': item.is_guest != 1,
                            }"
                        >
                            <UserCheck :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.enable')
                            }}</span>
                        </div>

                        <!-- Supprimer -->
                        <div
                            @click="item.is_guest == 1 ? null : deleteSingleItem(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors"
                            :class="{
                                'ppv2-pointer-events-none ppv2-opacity-50 ppv2-cursor-not-allowed': item.is_guest == 1,
                                'ppv2-cursor-pointer': item.is_guest != 1,
                            }"
                        >
                            <Trash2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('customers.delete')
                            }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('customers.delete') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- CSV Import Dialog -->
        <CustomDialog
            v-model="showImportPopup"
            :defaultTitle="null"
            heightClass="ppv2-max-h-[90vh]"
            width="88%"
            customClassTitle="poppinsSemiBold"
            :hideHeader="true"
            :hideFooter="true"
        >
            <template #content>
                <div class="ppv2-p-6">
                    <!-- Custom Header -->
                    <div class="ppv2-flex ppv2-items-start ppv2-justify-between ppv2-mb-4">
                        <div>
                            <h3 class="poppinsSemiBold ppv2-text-black ppv2-text-xl !ppv2-mb-0">
                                {{ $t('customers.importModal.title') }}
                            </h3>
                            <p class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-my-1">
                                {{ $t('customers.importModal.subtitle') }}
                            </p>
                        </div>
                        <button
                            type="button"
                            @click="closeImportPopup"
                            class="ppv2-text-black ppv2-text-opacity-50 hover:ppv2-text-opacity-60 ppv2-p-1"
                        >
                            <X :size="20" />
                        </button>
                    </div>

                    <!-- Template download link -->
                    <div class="ppv2-mb-6">
                        <button
                            type="button"
                            @click="downloadTemplate"
                            :disabled="analyzing || committing"
                            class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-underline ppv2-text-sm text-purple ppv2-transition disabled:ppv2-opacity-50"
                        >
                            <File :size="16" />
                            {{ $t('customers.importModal.downloadTemplate') }}
                        </button>
                    </div>

                    <!-- Drag & Drop Zone -->
                    <div v-if="!analysis && !commitResult && !analyzing" class="ppv2-mb-4">
                        <CustomInputUpload
                            :value="csvFile"
                            @input="onImportFileChange"
                            accept=".csv,text/csv"
                            :maxSize="50 * 1024 * 1024"
                            :requiredTypeFileText="$t('customers.importModal.csvOnly')"
                            :errorText="importError"
                            classContainerAdd="ppv2-opacity-100"
                        />
                    </div>

                    <!-- Analyzing spinner -->
                    <div v-if="analyzing" class="ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-py-12">
                        <div
                            class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent ppv2-mb-3"
                        ></div>
                        <p class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-60">
                            {{ $t('customers.importModal.analyzing') }}
                        </p>
                    </div>

                    <!-- Error Alert -->
                    <div
                        v-if="importError"
                        class="ppv2-mt-4 ppv2-rounded-2xl ppv2-border ppv2-border-red-100 ppv2-bg-red-50 ppv2-p-4 ppv2-text-sm ppv2-text-red-700"
                    >
                        {{ importError }}
                    </div>

                    <!-- Phase 2: Review / correct before committing -->
                    <div
                        v-if="analysis"
                        class="ppv2-mt-4 ppv2-rounded-2xl ppv2-border border-black bg-purple ppv2-bg-opacity-10 ppv2-p-4"
                    >
                        <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-flex-wrap ppv2-gap-2">
                            <div class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-70">
                                <span class="poppinsBold ppv2-text-purple-800">{{
                                    $t('customers.importModal.reviewTitle')
                                }}</span>
                                {{ $t('customers.importModal.ready', { count: analysis.summary.to_create }) }} ·
                                <span class="ppv2-text-amber-700 poppinsMedium">{{
                                    $t('customers.importModal.autofilled', { count: analysis.summary.autofilled })
                                }}</span>
                                · {{ $t('customers.importModal.duplicates', { count: analysis.summary.duplicate }) }} ·
                                {{ $t('customers.importModal.existing', { count: analysis.summary.existing }) }}
                                {{ $t('customers.importModal.totalLines', { count: analysis.summary.total }) }}
                            </div>
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                <button
                                    type="button"
                                    @click="cancelImport"
                                    :disabled="committing"
                                    class="ppv2-rounded-xl ppv2-border ppv2-border-black ppv2-border-opacity-40 ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-text-black ppv2-text-opacity-70 hover:ppv2-bg-black hover:ppv2-bg-opacity-10"
                                >
                                    {{ $t('customers.importModal.cancel') }}
                                </button>
                                <button
                                    type="button"
                                    @click="validateImport"
                                    :disabled="committing || analysis.rows.length === 0"
                                    :class="{
                                        'ppv2-opacity-60 ppv2-cursor-not-allowed': committing || analysis.rows.length === 0,
                                    }"
                                    class="ppv2-rounded-xl ppv2-px-4 ppv2-py-2 ppv2-text-sm poppinsSemiBold !ppv2-text-white bg-purple ppv2-bg-opacity-100"
                                >
                                    {{
                                        committing
                                            ? $t('customers.importModal.importing')
                                            : $t('customers.importModal.validateImport', { count: analysis.rows.length })
                                    }}
                                </button>
                            </div>
                        </div>

                        <!-- Progress Bar -->
                        <div v-if="committing && commitProgress" class="ppv2-mt-3">
                            <div
                                class="ppv2-flex ppv2-justify-between ppv2-text-xs ppv2-text-black ppv2-text-opacity-60 ppv2-mb-1"
                            >
                                <span>{{ $t('customers.importModal.importInProgress') }}</span>
                                <span>{{ commitProgress.done }} / {{ commitProgress.total }} ({{ commitProgressPct }}%)</span>
                            </div>
                            <div class="ppv2-h-2 ppv2-w-full ppv2-rounded-full ppv2-bg-purple-100 ppv2-overflow-hidden">
                                <div
                                    class="ppv2-h-2 ppv2-rounded-full ppv2-bg-purple-600 ppv2-transition-all"
                                    :style="{ width: commitProgressPct + '%' }"
                                ></div>
                            </div>
                        </div>

                        <!-- Flagged rows table -->
                        <div v-if="flaggedRows.length && !committing" class="ppv2-mt-3">
                            <div class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-60 ppv2-mb-1">
                                {{ $t('customers.importModal.flaggedRowsLabel') }}
                            </div>
                            <div
                                class="ppv2-max-h-72 ppv2-overflow-auto ppv2-rounded-xl ppv2-border ppv2-border-black ppv2-border-opacity-20"
                            >
                                <table class="ppv2-w-full ppv2-text-sm">
                                    <thead
                                        class="ppv2-sticky ppv2-top-0 headerColor ppv2-text-xs ppv2-text-black ppv2-text-opacity-60"
                                    >
                                        <tr>
                                            <th class="ppv2-px-2 ppv2-py-2 ppv2-text-left">
                                                {{ $t('customers.importModal.colLine') }}
                                            </th>
                                            <th class="ppv2-px-2 ppv2-py-2 ppv2-text-left">
                                                {{ $t('customers.importModal.colFirstName') }}
                                            </th>
                                            <th class="ppv2-px-2 ppv2-py-2 ppv2-text-left">
                                                {{ $t('customers.importModal.colLastName') }}
                                            </th>
                                            <th class="ppv2-px-2 ppv2-py-2 ppv2-text-left">
                                                {{ $t('customers.importModal.colEmail') }}
                                            </th>
                                            <th class="ppv2-px-2 ppv2-py-2 ppv2-text-left">
                                                {{ $t('customers.importModal.colPhone') }}
                                            </th>
                                            <th class="ppv2-px-2 ppv2-py-2 ppv2-text-left">
                                                {{ $t('customers.importModal.colCompleted') }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="row in flaggedRows"
                                            :key="row.line"
                                            class="ppv2-border-t ppv2-border-black ppv2-border-opacity-10"
                                        >
                                            <td class="ppv2-px-2 ppv2-py-1 ppv2-text-black ppv2-text-opacity-60">
                                                {{ row.line }}
                                            </td>
                                            <td class="ppv2-px-2 ppv2-py-1">
                                                <input
                                                    v-model="row.data.first_name"
                                                    :class="{
                                                        'ppv2-bg-black ppv2-bg-opacity-[5%]': row.autofilled.includes('name'),
                                                    }"
                                                    class="ppv2-w-full ppv2-rounded ppv2-border ppv2-border-black ppv2-border-opacity-10 ppv2-px-2 ppv2-py-1"
                                                />
                                            </td>
                                            <td class="ppv2-px-2 ppv2-py-1">
                                                <input
                                                    v-model="row.data.last_name"
                                                    :class="{
                                                        'ppv2-bg-black ppv2-bg-opacity-[5%]': row.autofilled.includes('name'),
                                                    }"
                                                    class="ppv2-w-full ppv2-rounded ppv2-border ppv2-border-black ppv2-border-opacity-10 ppv2-px-2 ppv2-py-1"
                                                />
                                            </td>
                                            <td class="ppv2-px-2 ppv2-py-1">
                                                <input
                                                    v-model="row.data.email"
                                                    :class="{
                                                        'ppv2-bg-black ppv2-bg-opacity-[5%]': row.autofilled.includes('email'),
                                                    }"
                                                    class="ppv2-w-full ppv2-rounded ppv2-border ppv2-border-black ppv2-border-opacity-10 ppv2-px-2 ppv2-py-1"
                                                />
                                            </td>
                                            <td class="ppv2-px-2 ppv2-py-1">
                                                <input
                                                    v-model="row.data.phone"
                                                    class="ppv2-w-full ppv2-rounded ppv2-border ppv2-border-black ppv2-border-opacity-10 ppv2-px-2 ppv2-py-1"
                                                />
                                            </td>
                                            <td class="ppv2-px-2 ppv2-py-1 ppv2-text-xs ppv2-text-amber-700">
                                                {{ row.autofilled.join(', ') }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Final result -->
                    <div
                        v-if="commitResult"
                        class="ppv2-mt-4 ppv2-rounded-2xl ppv2-border ppv2-border-green-100 ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-p-4 ppv2-text-sm ppv2-text-black ppv2-text-opacity-60"
                    >
                        <div class="poppinsBold ppv2-text-green-800 ppv2-mb-2">
                            {{ $t('customers.importModal.importSuccess') }}
                        </div>
                        <div class="ppv2-grid ppv2-grid-cols-2 md:ppv2-grid-cols-4 ppv2-gap-4 ppv2-text-center ppv2-my-4">
                            <div class="ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-rounded-xl ppv2-p-3 ppv2-shadow-sm">
                                <span class="ppv2-block ppv2-text-2xl poppinsBold ppv2-text-green-600">{{
                                    commitResult.created
                                }}</span>
                                <span class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-60">{{
                                    $t('customers.importModal.created')
                                }}</span>
                            </div>
                            <div class="ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-rounded-xl ppv2-p-3 ppv2-shadow-sm">
                                <span class="ppv2-block ppv2-text-2xl poppinsBold ppv2-text-black ppv2-text-opacity-70">{{
                                    commitResult.skipped_existing
                                }}</span>
                                <span class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-60">{{
                                    $t('customers.importModal.alreadyExisting')
                                }}</span>
                            </div>
                            <div class="ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-rounded-xl ppv2-p-3 ppv2-shadow-sm">
                                <span class="ppv2-block ppv2-text-2xl poppinsBold ppv2-text-amber-600">{{
                                    commitResult.skipped_duplicate
                                }}</span>
                                <span class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-60">{{
                                    $t('customers.importModal.doublons')
                                }}</span>
                            </div>
                            <div class="ppv2-bg-white dark:ppv2-bg-dark-popup ppv2-rounded-xl ppv2-p-3 ppv2-shadow-sm">
                                <span class="ppv2-block ppv2-text-2xl poppinsBold text-purple">{{ commitResult.total }}</span>
                                <span class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-60">{{
                                    $t('customers.importModal.totalImported')
                                }}</span>
                            </div>
                        </div>
                        <details v-if="commitResult.errors && commitResult.errors.length" class="ppv2-mt-3">
                            <summary class="ppv2-cursor-pointer poppinsMedium ppv2-text-red-700 hover:ppv2-underline">
                                {{ $t('customers.importModal.errorsLine', { count: commitResult.errors.length }) }}
                            </summary>
                            <ul
                                class="ppv2-mt-2 ppv2-list-disc ppv2-pl-5 ppv2-max-h-40 ppv2-overflow-auto ppv2-space-y-1 ppv2-text-red-600 ppv2-text-xs"
                            >
                                <li v-for="(err, i) in commitResult.errors" :key="i">{{ err }}</li>
                            </ul>
                        </details>
                    </div>

                    <!-- Initial State/Close button -->
                    <div class="ppv2-flex ppv2-justify-center ppv2-mt-6" v-if="commitResult && !committing && !analyzing">
                        <button
                            type="button"
                            @click="closeImportPopup"
                            class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-rounded-xl bg-purple ppv2-px-6 ppv2-py-3 ppv2-text-sm poppinSemiBold !ppv2-text-white hover:ppv2-bg-purple-700 ppv2-transition ppv2-shadow-lg"
                        >
                            {{ $t('customers.importModal.close') }}
                        </button>
                    </div>
                </div>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import {
    KeyRound,
    Computer,
    SquarePen,
    UserX,
    UserCheck,
    CalendarPlus,
    Trash2,
    Plus,
    FilePen,
    ChevronDown,
    GitMerge,
    MoreVertical,
    Upload,
    UploadCloud,
    File,
    X,
} from 'lucide-vue'
import { eventBus } from '../../Customer/eventBus'
import DataTable from '../../Shared/DataTable.vue'
import Search from '../Experience/components/Search.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'CustomerTable',
    components: {
        KeyRound,
        Computer,
        SquarePen,
        UserX,
        UserCheck,
        CalendarPlus,
        Trash2,
        Plus,
        FilePen,
        ChevronDown,
        GitMerge,
        MoreVertical,
        Upload,
        UploadCloud,
        File,
        X,
        DataTable,
        Search,
        BaseCheckbox,
        BaseButton,
        CustomDialog,
        ExportSelect,
        CustomInputUpload,
    },
    props: {
        userId: {
            type: [Number, String, null],
            required: false,
            default: null,
        },
        token: {
            type: String,
            required: true,
        },
        planPro: {
            type: Object,
            default: () => ({}),
        },
        tenant: {
            type: Object,
            default: () => ({}),
        },
        isSuperAdmin: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            search: '',
            tableData: [],
            itemsPerPage: getPerPage('customers_items_per_page', 50),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            exportLoading: false,
            showColumns: false,
            selectedItems: [],
            selectAll: false,
            showDeletePopup: false,
            deletePopupTitle: this.$t('customers.deletePopupTitle'),
            deletePopupDescription: '',
            // CSV Import state
            showImportPopup: false,
            analyzing: false,
            committing: false,
            analysis: null,
            commitResult: null,
            commitProgress: null,
            importError: null,
            csvFile: null,
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null,
            activeActionMenu: null, // Track which action menu is open
            dropdownPosition: { top: '0px', left: '0px' },
            sortColumn: null,
            sortDirection: 'asc',
            // Default columns based on screenshot: Prénom, Nom, Adresse email, Téléphone, Montant dépensé, Dernière venue
            selectedColumnKeys: [
                'first_name',
                'last_name',
                'email',
                'phone_number',
                'social_reason',
                'communication',
                'spent',
                'last_coming',
                'actions',
            ],
        }
    },
    computed: {
        flaggedRows() {
            return this.analysis ? this.analysis.rows.filter((r) => r.status === 'autofilled') : []
        },
        commitProgressPct() {
            if (!this.commitProgress || !this.commitProgress.total) {
                return 0
            }
            return Math.round((this.commitProgress.done / this.commitProgress.total) * 100)
        },
        // Define all available columns
        allColumns() {
            return [
                { key: 'id', label: this.$t('customers.fields.id'), sortable: true, align: 'center' },
                {
                    key: 'first_name',
                    label: this.$t('customers.fields.firstName'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'last_name',
                    label: this.$t('customers.fields.lastName'),
                    sortable: true,
                    type: 'text',
                },
                { key: 'email', label: this.$t('customers.fields.email'), sortable: true, type: 'text' },
                {
                    key: 'phone_number',
                    label: this.$t('customers.fields.phone'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'type_customer',
                    label: this.$t('customers.fields.type_customer'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'social_reason',
                    label: this.$t('customers.fields.social_reason'),
                    sortable: true,
                    type: 'text',
                },
                { key: 'birthday', label: this.$t('customers.fields.birthday'), sortable: true, type: 'date' },
                { key: 'age', label: this.$t('customers.fields.age'), sortable: true, type: 'number' },
                { key: 'gender', label: this.$t('customers.fields.gender'), sortable: true, type: 'text' },
                {
                    key: 'account',
                    label: this.$t('customers.fields.account'),
                    sortable: true,
                    type: 'text',
                },
                { key: 'origin', label: this.$t('customers.fields.origin'), sortable: true, type: 'text' },
                {
                    key: 'spent',
                    label: this.$t('customers.fields.spent'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'loyalty_points',
                    label: this.$t('customers.fields.loyaltyPoints'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'first_coming',
                    label: this.$t('customers.fields.firstComing'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'last_coming',
                    label: this.$t('customers.fields.lastComing'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'created_at',
                    label: this.$t('customers.fields.createdAt'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'communication',
                    label: this.$t('customers.fields.communication'),
                    sortable: true,
                    type: 'text',
                },
                { key: 'actions', label: this.$t('customers.fields.actions'), sortable: false, type: 'actions' },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
        hasAccess() {
            return Boolean(this.access_plan(this.tenant?.plan_id, this.planPro))
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        navigateToCreate() {
            window.location.href = '/back-office/v3/customer/create'
        },
        openImportPopup() {
            this.showImportPopup = true
            this.analysis = null
            this.commitResult = null
            this.importError = null
            this.analyzing = false
            this.committing = false
            this.commitProgress = null
            this.csvFile = null
        },
        closeImportPopup() {
            this.showImportPopup = false
            this.analysis = null
            this.commitResult = null
            this.importError = null
            this.analyzing = false
            this.committing = false
            this.commitProgress = null
            this.csvFile = null
        },
        async downloadTemplate() {
            this.importError = null
            try {
                const response = await axios.get('/api/v3/customer/import/template', {
                    headers: { Authorization: `Bearer ${this.token}` },
                    responseType: 'blob',
                })
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                link.download = 'modele_import_clients.csv'
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            } catch (e) {
                this.importError = this.$t('customers.importModal.downloadError')
            }
        },
        async onImportFileChange(file) {
            this.csvFile = file
            if (!file) {
                return
            }
            this.analyzeFile(file)
        },
        async analyzeFile(file) {
            this.analyzing = true
            this.analysis = null
            this.commitResult = null
            this.importError = null
            try {
                const formData = new FormData()
                formData.append('file', file)
                const response = await axios.post('/api/v3/customer/import/analyze', formData, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                })
                this.analysis = response.data
            } catch (err) {
                this.importError = this.importErrorMessage(err)
            } finally {
                this.analyzing = false
            }
        },
        cancelImport() {
            this.analysis = null
            this.importError = null
            this.csvFile = null
        },
        async validateImport() {
            if (!this.analysis || this.analysis.rows.length === 0) {
                return
            }
            const rows = this.analysis.rows
            const total = rows.length
            const CHUNK = 300
            const agg = { total: 0, created: 0, skipped_existing: 0, skipped_duplicate: 0, errors: [] }

            this.committing = true
            this.importError = null
            this.commitResult = null
            this.commitProgress = { done: 0, total }

            try {
                for (let i = 0; i < total; i += CHUNK) {
                    const chunk = rows.slice(i, i + CHUNK)
                    const response = await axios.post(
                        '/api/v3/customer/import/commit',
                        { rows: chunk },
                        { headers: { Authorization: `Bearer ${this.token}` } }
                    )
                    const s = response.data.summary
                    agg.total += s.total
                    agg.created += s.created
                    agg.skipped_existing += s.skipped_existing
                    agg.skipped_duplicate += s.skipped_duplicate
                    if (s.errors && s.errors.length) {
                        agg.errors.push(...s.errors)
                    }
                    this.commitProgress = { done: Math.min(i + CHUNK, total), total }
                }
                agg.errors = agg.errors.slice(0, 100)
                this.commitResult = agg
                this.analysis = null

                // Refresh list and stats
                this.fetchData(this.getParams())
                this.$emit('refresh-stats')
            } catch (err) {
                agg.errors = agg.errors.slice(0, 100)
                this.commitResult = agg
                this.importError =
                    this.importErrorMessage(err) +
                    ` (interrompu après ${agg.created} créés — relancez pour reprendre, les clients déjà créés seront ignorés)`
            } finally {
                this.committing = false
                this.commitProgress = null
            }
        },
        importErrorMessage(err) {
            const status = err.response && err.response.status
            if (status === 403) {
                return 'Action réservée au super-administrateur.'
            }
            if (err.response && err.response.data && err.response.data.message) {
                return err.response.data.message
            }
            return "Échec de l'import du fichier."
        },
        toggleColumn(key) {
            if (this.selectedColumnKeys.includes(key)) {
                this.selectedColumnKeys = this.selectedColumnKeys.filter((k) => k !== key)
            } else {
                this.selectedColumnKeys.push(key)
            }
            const params = this.getParams()
            this.fetchData(params)
        },
        handlePageChange(page) {
            this.currentPage = page
            const params = this.getParams()
            this.fetchData(params)
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Math.min(Number(perPage) || 10, 100)
            setPerPage('customers_items_per_page', this.itemsPerPage)
            const params = this.getParams()
            this.fetchData(params)
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            clearTimeout(this._searchDebounce)
            this._searchDebounce = setTimeout(() => {
                const params = this.getParams()
                this.fetchData(params)
            }, 350)
        },
        handleSort(key) {
            if (this.sortColumn === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = key
                this.sortDirection = 'asc'
            }
            const params = this.getParams()
            this.fetchData(params)
        },
        handleClickOutside(event) {
            if (this.$refs.columnsDropdown && !this.$refs.columnsDropdown.contains(event.target)) {
                this.showColumns = false
            }

            // Close action menu if clicking outside
            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]

                // Handle both array and non-array refs (Vue 2 vs Vue 3 or v-for behavior)
                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu

                const clickedButton = btnEl && (btnEl === event.target || btnEl.contains(event.target))
                const clickedMenu = menuEl && (menuEl === event.target || menuEl.contains(event.target))

                if (!clickedButton && !clickedMenu) {
                    this.activeActionMenu = null
                }
            }
        },

        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },

        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 is min-width of dropdown
            }
        },

        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },

        getParams() {
            const params = {
                search: this.search,
                currentPage: this.currentPage,
                itemsPerPage: Math.min(this.itemsPerPage || 10, 100),
                columns: this.visibleColumns.map((c) => c.key),
            }

            if (this.sortColumn) {
                params.sortBy = this.sortColumn
                params.sortDirection = this.sortDirection
            }

            return params
        },

        async fetchData(params) {
            const seq = ++this._fetchSeq
            this.loading = true
            try {
                const response = await axios.get('/api/v3/customer/list', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                if (seq !== this._fetchSeq) return
                const data = response.data

                this.recordsTotal = data.total
                this.tableData = data.data || []
            } catch (error) {
                if (seq !== this._fetchSeq) return
                this.$toast.error(error?.response?.data?.message ?? this.$t('datatable.error'))
            } finally {
                if (seq === this._fetchSeq) this.loading = false
            }
        },

        async downloadCustomers(format = 'csv') {
            this.exportLoading = true
            try {
                const params = this.getParams()
                params.format = format
                const response = await axios.get('/api/v3/customer/export', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                    responseType: 'blob',
                })

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `customers-${Date.now()}.xlsx`)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
                this.$toast?.success?.(this.$t('customers.exportSuccess'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.exportLoading = false
            }
        },

        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.tableData.length
        },

        toggleSelectAll() {
            const allIds = this.tableData.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },

        deleteSingleItem(itemId) {
            this.activeActionMenu = null // Close menu
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('customers.deletePopupDescriptionSingle')
            this.showDeletePopup = true
        },

        async confirmDelete() {
            if (this.deleteAction === 'single' && this.itemToDelete) {
                try {
                    await axios.delete('/api/v3/customer/' + this.itemToDelete, {
                        headers: { Authorization: `Bearer ${this.token}` },
                    })
                    await this.fetchData(this.getParams())
                    this.$emit('refresh-stats')
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    this.$toast.error(error?.response?.data?.message ?? this.$t('customers.deleteError'))
                }
            }
            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },

        handleDisable(id) {
            this.activeActionMenu = null // Close menu
            this.$modal.show('dialog', {
                title: 'Alert',
                text: `<p class="mb-0 p-3">${this.$t('customers.disableConfirmTitle')}</p>`,
                buttons: [
                    {
                        title: `<b class="btn btn-dark width-btn height_btn_page rounded-12">${this.$t('customers.disable')}</b>`,
                        handler: () => {
                            this.disable(id)
                        },
                    },
                    {
                        title: `<b class="btn btn-outline-dark width-btn height_btn_page rounded-12">${this.$t(
                            'common.close'
                        )}</b>`,
                        handler: () => {
                            this.$modal.hide('dialog')
                        },
                    },
                ],
            })
        },

        async disable(id) {
            this.loading = true
            try {
                const res = await axios.patch(
                    `/api/v3/customer/${id}/disable`,
                    {},
                    {
                        headers: { Authorization: `Bearer ${this.token}` },
                    }
                )
                if (res?.data?.success) {
                    this.$toast.success(this.$t('customers.disableSuccess'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error disable:', error)
                this.$toast.error(error?.response?.data?.message ?? this.$t('datatable.error'))
            } finally {
                this.loading = false
                const params = this.getParams()
                this.fetchData(params)
                this.$modal.hide('dialog')
            }
        },

        handleEnable(id) {
            this.activeActionMenu = null // Close menu
            this.$modal.show('dialog', {
                title: 'Alert',
                text: this.$t('customers.enableConfirmTitle'),
                buttons: [
                    {
                        title: this.$t('customers.enable'),
                        handler: () => {
                            this.enable(id)
                        },
                    },
                    {
                        title: `<b>${this.$t('common.close')}</b>`,
                        handler: () => {
                            this.$modal.hide('dialog')
                        },
                    },
                ],
            })
        },

        async enable(id) {
            this.loading = true
            try {
                const res = await axios.patch(`/api/v3/customer/${id}/enable`)
                if (res?.data?.success) {
                    this.$toast.success(this.$t('customers.enableSuccess'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error enable:', error)
                this.$toast.error(error?.response?.data?.message ?? this.$t('datatable.error'))
            } finally {
                this.loading = false
                const params = this.getParams()
                this.fetchData(params)
                this.$modal.hide('dialog')
            }
        },
        access_plan(user_plan_id, plan) {
            if (!plan) return false
            return user_plan_id >= plan.min_plan_level || plan.is_given_by_super_admin
        },
    },
    mounted() {
        this._fetchSeq = 0
        const params = this.getParams()
        this.fetchData(params)
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updateDropdownPosition, true)
        window.addEventListener('resize', this.updateDropdownPosition)
    },
    beforeDestroy() {
        clearTimeout(this._searchDebounce)
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.tableData.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },

        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.menuDropdown {
    background: white;
    color: #94a3b8;
    margin-top: 10px;
    z-index: 50;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdownItem:hover {
    background-color: #1a1f37;
    color: white !important;
}

.dropdownItem:hover * {
    color: white !important;
}

.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}

.dark {
    .menuDropdown {
        background-color: #2c2c2ce5;
    }

    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }

    .actionItem:hover * {
        color: white !important;
    }
}

.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}

.vue-dialog {
    font-size: clamp(12px, 0.729545vw, 16px);
    width: 100% !important;
    padding-bottom: 35px;
    text-align: center;
    max-width: 600px;
    left: 0 !important;
    margin: auto;
}

.vue-dialog-button b {
    line-height: 44px;
}

.vue-dialog-buttons {
    border-top: none !important;
    gap: 20px;
}

.vue-dialog-button:not(:first-of-type) {
    border-left: none !important;
}

.vue-dialog-content-title {
    min-height: 1.75rem;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.75rem;
    border-bottom: 1px solid #eee;
}

.vue-dialog-content {
    padding: 14px 0;
}
.headerColor {
    background-color: #e1ceef;
}
.dark .headerColor {
    background-color: #2d195f;
}
@media (max-width: 767.98px) {
    .vue-dialog-button b {
        line-height: 30px;
    }
}
</style>
